import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Button from '../components/Button';
import Card from '../components/Card';
import { theme } from '../theme/theme';
import useUsers from '../hooks/useUsers';

const Users = () => {
  const [dateFilter, setDateFilter] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showNumberSearch, setShowNumberSearch] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  const {
    users,
    loading,
    error,
    fetchUsers,
    getDateRange,
    formatDate,
    totalPages,
    currentPage,
    setCurrentPage,
    limit,
    setLimit,
    fetchUserByMobile
  } = useUsers();

  // Fetch last week's users on mount
  useEffect(() => {
    const { fromDate, toDate } = getDateRange('week');
    fetchUsers({ fromDate, toDate, page: 1, limit, sortBy, sortOrder });
    setDateFilter('week');
  }, []);

  // Refetch when page, sort, or limit changes
  useEffect(() => {
    if (dateFilter && typeof dateFilter === 'string') {
      const { fromDate, toDate } = getDateRange(dateFilter);
      fetchUsers({ fromDate, toDate, page: currentPage, limit, sortBy, sortOrder });
    }
  }, [currentPage, limit, sortBy, sortOrder]);

  const handleFilterChange = (filter) => {
    const { fromDate, toDate } = getDateRange(filter);
    setDateFilter(filter);
    setShowDatePicker(false);
    setShowNumberSearch(false);
    setCurrentPage(1);
    fetchUsers({ fromDate, toDate, page: 1, limit, sortBy, sortOrder });
  };

  const handleSetDate = () => {
    setShowDatePicker(true);
    setShowNumberSearch(false);
    setDateFilter(null);
  };

  const handleSearchByNumber = () => {
    setShowNumberSearch(true);
    setShowDatePicker(false);
    setDateFilter(null);
  };

  const handleDateSubmit = () => {
    if (startDate && endDate) {
      setDateFilter({ startDate, endDate });
      setShowDatePicker(false);
      setCurrentPage(1);
      fetchUsers({ fromDate: startDate, toDate: endDate, page: 1, limit, sortBy, sortOrder });
    }
  };

  const handleNumberSearch = () => {
    if (mobileNumber) {
      setDateFilter({ mobileNumber });
      setShowNumberSearch(false);
      setCurrentPage(1);
      fetchUserByMobile(mobileNumber);
    }
  };

  // Pagination handlers
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  // Sorting handler
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: theme.colors.background.default }}>
      <Sidebar active="users" title="Users" />
      <main style={{ flex: 1, padding: `${theme.spacing.md} ${theme.spacing.xl} ${theme.spacing.xl} ${theme.spacing.xl}`, marginLeft: 280, paddingLeft: theme.spacing.xl }}>
        <h2 style={{ ...theme.typography.h2, marginBottom: theme.spacing.sm }}>Users</h2>
        
        {/* Filter Section */}
        <Card 
          elevation={2}
          padding="md"
          style={{
            marginBottom: theme.spacing.lg,
            position: 'sticky',
            top: 0,
            zIndex: 2
          }}
        >
          {!showDatePicker && !showNumberSearch ? (
            <div style={{ display: 'flex', gap: theme.spacing.md }}>
              <Button
                variant={dateFilter === 'week' ? 'contained' : 'outlined'}
                color="primary"
                onClick={() => handleFilterChange('week')}
              >
                Last Week
              </Button>
              <Button
                variant={dateFilter === 'month' ? 'contained' : 'outlined'}
                color="primary"
                onClick={() => handleFilterChange('month')}
              >
                Last Month
              </Button>
              <Button
                variant={showDatePicker ? 'contained' : 'outlined'}
                color="primary"
                onClick={handleSetDate}
              >
                Set Date
              </Button>
              <Button
                variant={showNumberSearch ? 'contained' : 'outlined'}
                color="primary"
                onClick={handleSearchByNumber}
              >
                Search by Number
              </Button>
            </div>
          ) : showDatePicker ? (
            <div style={{ 
              display: 'flex',
              gap: theme.spacing.md,
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', gap: theme.spacing.md, alignItems: 'center' }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: theme.spacing.xs,
                    color: theme.colors.text.secondary,
                    fontSize: theme.typography.body2.fontSize
                  }}>
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{
                      padding: theme.spacing.sm,
                      border: `1px solid ${theme.colors.grey[300]}`,
                      borderRadius: theme.borderRadius.sm,
                      fontSize: theme.typography.body2.fontSize
                    }}
                  />
                </div>
                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: theme.spacing.xs,
                    color: theme.colors.text.secondary,
                    fontSize: theme.typography.body2.fontSize
                  }}>
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{
                      padding: theme.spacing.sm,
                      border: `1px solid ${theme.colors.grey[300]}`,
                      borderRadius: theme.borderRadius.sm,
                      fontSize: theme.typography.body2.fontSize
                    }}
                  />
                </div>
              </div>
              <Button
                variant="contained"
                color="primary"
                onClick={handleDateSubmit}
                disabled={!startDate || !endDate}
              >
                Apply
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  setShowDatePicker(false);
                  setStartDate('');
                  setEndDate('');
                }}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <div style={{ 
              display: 'flex',
              gap: theme.spacing.md,
              alignItems: 'center'
            }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: theme.spacing.xs,
                  color: theme.colors.text.secondary,
                  fontSize: theme.typography.body2.fontSize
                }}>
                  Mobile Number
                </label>
                <input
                  type="tel"
                  value={mobileNumber}
                  onChange={(e) => setMobileNumber(e.target.value)}
                  placeholder="Enter mobile number"
                  style={{
                    padding: theme.spacing.sm,
                    border: `1px solid ${theme.colors.grey[300]}`,
                    borderRadius: theme.borderRadius.sm,
                    fontSize: theme.typography.body2.fontSize,
                    width: '200px'
                  }}
                />
              </div>
              <Button
                variant="contained"
                color="primary"
                onClick={handleNumberSearch}
                disabled={!mobileNumber}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  setShowNumberSearch(false);
                  setMobileNumber('');
                }}
              >
                Cancel
              </Button>
            </div>
          )}
        </Card>

        {/* Users Table */}
        <Card elevation={2} padding="md">
          {loading ? (
            <div style={{ textAlign: 'center', padding: theme.spacing.xl }}>
              Loading...
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: theme.spacing.xl, color: theme.colors.error }}>
              {error}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: theme.borderRadius.md, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${theme.colors.grey[300]}`, background: theme.colors.grey[100] }}>
                  <th style={{ textAlign: 'left', padding: theme.spacing.md, cursor: 'pointer' }} onClick={() => handleSort('firstName')}>
                    User Name {sortBy === 'firstName' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>
                  <th style={{ textAlign: 'left', padding: theme.spacing.md, cursor: 'pointer' }} onClick={() => handleSort('phone')}>
                    Mobile Number {sortBy === 'phone' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>
                  <th style={{ textAlign: 'left', padding: theme.spacing.md, cursor: 'pointer' }} onClick={() => handleSort('lastAppLoggedIn')}>
                    Last App Login {sortBy === 'lastAppLoggedIn' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>
                  <th style={{ textAlign: 'left', padding: theme.spacing.md, cursor: 'pointer' }} onClick={() => handleSort('lastWebLoggedIn')}>
                    Last Web Login {sortBy === 'lastWebLoggedIn' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>
                  <th style={{ textAlign: 'left', padding: theme.spacing.md, cursor: 'pointer' }} onClick={() => handleSort('createdAt')}>
                    Created At {sortBy === 'createdAt' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>
                  <th style={{ textAlign: 'left', padding: theme.spacing.md, cursor: 'pointer' }} onClick={() => handleSort('location')}>
                    Location {sortBy === 'location' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: theme.spacing.xl, color: theme.colors.text.secondary }}>
                      No users found for the selected filter.
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.userId} style={{ borderBottom: `1px solid ${theme.colors.grey[200]}` }}>
                      <td style={{ padding: theme.spacing.md, fontWeight: 500 }}>
                        {`${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A'}
                      </td>
                      <td style={{ padding: theme.spacing.md }}>
                        {user.countryCode && user.phone ? `${user.countryCode}${user.phone}` : 'N/A'}
                      </td>
                      <td style={{ padding: theme.spacing.md }}>
                        {formatDate(user.lastAppLoggedIn)}
                      </td>
                      <td style={{ padding: theme.spacing.md }}>
                        {formatDate(user.lastWebLoggedIn)}
                      </td>
                      <td style={{ padding: theme.spacing.md }}>
                        {formatDate(user.createdAt)}
                      </td>
                      <td style={{ padding: theme.spacing.md }}>
                        {user.location || 'N/A'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </Card>

        {/* Pagination Controls */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: theme.spacing.lg, gap: theme.spacing.md }}>
          <Button onClick={handlePrevPage} disabled={currentPage === 1}>
            Previous
          </Button>
          <span style={{ color: theme.colors.text.secondary }}>
            Page {currentPage} of {totalPages}
          </span>
          <Button onClick={handleNextPage} disabled={currentPage === totalPages}>
            Next
          </Button>
        </div>
      </main>
    </div>
  );
};

export default Users; 